// 신청/계약 Table Columns
import moment from "moment";
import { Cell } from "react-table";
import { ContractApprovalStatus, searchStatusOption } from "src/api/contract/contract-types";
import { ProductApprovalType } from "src/api/product/product-types";
import { BaseTooltip } from "src/components";
import { BaseContractStatus } from "src/components/BaseContractStatus";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import DetailLink from "src/components/DetailLink";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import PagePath from "src/pagePath.json";
import { ProductTypeLabels } from "src/pages/product/product-types";
import { calculateDate, ViewYmdFormat } from "src/utils/common-util";

const getContractStatusLabel = (value: string) => {
  switch (value) {
    case "PENDING":
      return "승인대기";
    case "APPROVED":
      return "승인완료";
    case "REJECTED":
      return "반려";
    default:
      return "-";
  }
};

const getApprovalTypeLabel = (value: ProductApprovalType) => {
  switch (value) {
    case "AUTO":
      return "자동승인";
    case "ADMIN":
      return "관리자승인";
    default:
      return value;
  }
};

// 숫자값 표기 (0 포함, 미존재는 "-")
const numberOrDash = (value?: number | null) => (value !== undefined && value !== null ? value : "-");

export const columns = [
  {
    Header: "계약 ID",
    accessor: "contractId",
    sticky: "left",
    width: 120,
    Cell: (props: any) => {
      const isS2Contract = props.isS2Contract;
      const contractDetailPath = PagePath.contract.detail.replace(":id", props.row.original.contractId);
      const contractS2DetailPath = PagePath.contractS2.detail.replace(":id", props.row.original.contractId);
      return (
        <>
          <DetailLink to={isS2Contract ? contractS2DetailPath : contractDetailPath} className="text-underline">
            {props.value}
          </DetailLink>
        </>
      );
    },
  },
  {
    // 신청번호(난수) 대체 컬럼. 계약 ID에 이미 계약상세 링크가 있어 계약관리 ID는 링크를 걸지 않는다.
    Header: "계약관리 ID",
    accessor: "contractManageId",
    width: 120,
    sticky: "left",
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={String(value)} /> : "-"}</div>;
    },
    excelValue: (row: any) => {
      return row.contractManageId ? String(row.contractManageId) : "-";
    },
  },
  {
    Header: "계약상태",
    accessor: "contractStep",
    sticky: "left",
    width: 80,
    Cell: ({ value }: any) => {
      return (
        <div className="w-100 flex-center-center">
          <BaseContractStatus status={value} />
        </div>
      );
    },
    excelValue: (row: any) => {
      const status = searchStatusOption.find((option) => option.value === row.contractStep)?.label;
      return status ? status : "-";
    },
  },
  {
    Header: "승인유형",
    accessor: "approvalType",
    sticky: "left",
    width: 80,
    Cell: ({ value }: { value: ProductApprovalType }) => {
      return <div className="w-100 text-center">{getApprovalTypeLabel(value)}</div>;
    },
    excelValue: (row: any) => {
      return getApprovalTypeLabel(row.approvalType);
    },
  },
  {
    Header: "승인상태",
    accessor: "approvalStatus",
    sticky: "left",
    width: 80,
    Cell: ({ value }: { value: ContractApprovalStatus }) => {
      return <div className="w-100 text-center">{getContractStatusLabel(value)}</div>;
    },
    excelValue: (row: any) => {
      return getContractStatusLabel(row.approvalStatus);
    },
  },
  {
    Header: "계약자(법인명/상호)",
    accessor: "mbOrganizationName",
    width: 160,
    sticky: "left",
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    Header: "계약자명",
    accessor: "contractorName",
    width: 120,
    sticky: "left",
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    Header: "계약명(필수)",
    accessor: "contractName",
    width: 160,
    sticky: "left",
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    Header: "계약명(선택)",
    accessor: "contractNickname",
    width: 160,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-left">{value ? <BaseTooltip contents={value} /> : "-"}</div>;
    },
  },
  {
    // 컬럼 순서 변경: 계약명 뒤, 상품명 앞으로 배치
    Header: "이용신청서",
    accessor: "applicationId",
    width: 100,
    Cell: ({ value, row, openApplicationModal }: Cell<any> & { openApplicationModal: (contractId: number) => void }) => {
      if (value) {
        const contractId = row.original.contractId;
        return (
          <div className="w-100 text-center">
            <span className="text-blue text-underline cursor-pointer" onClick={() => openApplicationModal(contractId)}>
              보기
            </span>
          </div>
        );
      } else {
        return <div className="w-100 text-center">-</div>;
      }
    },
    // 엑셀 추출시 신청서 유무에 따라 Y/N
    excelValue: (row: any) => {
      return row.applicationId ? "Y" : "N";
    },
  },
  {
    Header: "상품명",
    accessor: "spaceProductName",
    width: 160,
    Cell: ({ value }: any) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value} />
        </div>
      );
    },
  },
  {
    Header: "상품유형",
    accessor: "spaceProductType",
    width: 80,
    Cell: (props: any) => {
      return (
        <div className="w-100 flex-center-center">
          <ProductTypeChangeLabel productType={props.value} />
        </div>
      );
    },
    excelValue: (row: any) => {
      return ProductTypeLabels[row.spaceProductType as keyof typeof ProductTypeLabels] || "-";
    },
  },
  {
    Header: "상품 ID",
    accessor: "spaceProductId",
    width: 120,
    Cell: (props: any) => {
      const path = PagePath.product.detail.replace(":id", props.row.original.spaceProductId);
      return (
        <>
          <BaseNewTabLink path={path} value={props.value || "-"} ellipsis />
        </>
      );
    },
  },
  {
    // 이용자(현재/최대) 분리: 이용자 수(명) - 우측 정렬, 단위 제거, 이용자 목록 연결링크 유지
    Header: "이용자 수(명)",
    accessor: "memberTotCount",
    width: 110,
    Cell: ({ row }: any) => {
      const now = row.original.memberTotCount;
      const path = PagePath.user.list + "?contractApplyNumber=" + row.original?.contractApplyNumber + "&contractId=" + row.original?.contractId;
      return <BaseNewTabLink path={path} value={now !== undefined && now !== null ? String(now) : "-"} ellipsis className="w-100 flex-center-end" />;
    },
    excelValue: (row: any) => {
      return numberOrDash(row.memberTotCount);
    },
  },
  {
    // 이용자(현재/최대) 분리: 정원(명) - 우측 정렬, 단위 제거
    Header: "정원(명)",
    accessor: "memberMaxNums",
    width: 100,
    Cell: ({ row }: any) => {
      return <p className="w-100 text-right">{numberOrDash(row.original.memberMaxNums)}</p>;
    },
    excelValue: (row: any) => {
      return numberOrDash(row.memberMaxNums);
    },
  },
  {
    Header: "이용 시작일시",
    accessor: "contractStartTime",
    width: 160,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-center">{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</div>;
    },
    excelValue: (row: any) => {
      return row.contractStartTime ? moment(row.contractStartTime).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-";
    },
  },
  {
    Header: "이용 종료일시",
    accessor: "contractEndTime",
    width: 160,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-center">{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</div>;
    },
    excelValue: (row: any) => {
      return row.contractEndTime ? moment(row.contractEndTime).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-";
    },
  },
  {
    Header: "이용기간",
    accessor: "contractTerm",
    width: 80,
    Cell: ({ row }: any) => {
      const date = calculateDate(row.original.contractStartTime!, row.original.contractEndTime!, row.original.spaceProductType!);

      return (
        <div className="w-100 text-left">
          <p>{date !== "" ? date : "-"}</p>
        </div>
      );
    },
    excelValue: (row: any) => {
      const date = calculateDate(row.contractStartTime, row.contractEndTime, row.spaceProductType);
      return date !== "" ? date : "-";
    },
  },
  {
    // 최초 등록일 → 최초 등록일시, 날짜+시간 표기, 호버 제거, 가운데 정렬
    Header: "최초 등록일시",
    accessor: "createdDate",
    width: 160,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-center">{value ? moment(value).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</div>;
    },
    excelValue: (row: any) => {
      return row.createdDate ? moment(row.createdDate).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-";
    },
  },
  {
    // 최종 수정일 → 최종 수정일시, 날짜+시간 표기, 호버 제거, 가운데 정렬
    Header: "최종 수정일시",
    accessor: "modifiedDate",
    width: 160,
    Cell: ({ value }: any) => {
      return <div className="w-100 text-center">{value ? moment(value).format(ViewYmdFormat.FULL) : "-"}</div>;
    },
    excelValue: (row: any) => {
      return row.modifiedDate ? moment(row.modifiedDate).format(ViewYmdFormat.FULL) : "-";
    },
  },
];
